import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Lock, ShieldCheck, ShieldAlert, MessageSquarePlus, X, Send } from "lucide-react";
import DashboardShell from "../components/layout/DashboardShell";
import { Button, Input, Pill, Select, TextArea } from "../components/common/Primitives";
import { Modal, ModalFooter } from "../components/common/Modal";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiError } from "../lib/api";

/**
 * Universal Account / Profile Settings.
 * Available at /settings/profile for every signed-in role.
 */
export default function AccountProfile() {
  const { user: me, refreshMe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [locks, setLocks] = useState({});
  const [saving, setSaving] = useState(false);
  const [changeReqOpen, setChangeReqOpen] = useState(null); // field name
  const [requests, setRequests] = useState([]);

  const load = useCallback(() => {
    api.get("/profile/me")
      .then((r) => { setProfile(r.data.user); setLocks(r.data.locks || {}); })
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/profile/me/change-requests")
      .then((r) => setRequests(r.data.items || []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!profile) {
    return (
      <DashboardShell title="Profile Settings" subtitle="ACCOUNT">
        <div className="text-ink-muted text-sm">Loading…</div>
      </DashboardShell>
    );
  }

  const isLocked = (field) => Boolean(locks[field]);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      ["mobile", "address", "city", "linkedin_url", "timezone", "avatar_url", "bio"].forEach((f) => {
        body[f] = profile[f] ?? "";
      });
      // Allow name / dob / gender / pan only when not locked
      ["name", "dob", "gender", "pan"].forEach((f) => {
        if (!isLocked(f)) body[f] = profile[f] ?? "";
      });
      await api.put("/profile/me", body);
      toast.success("Profile updated");
      await refreshMe?.();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const isInternal = ["admin", "manager", "agent"].includes(profile.role);
  const isSuperAdmin = profile.role === "super_admin";
  const kycState = profile.kyc_status;

  return (
    <DashboardShell title="Profile Settings" subtitle="ACCOUNT · IDENTITY">
      <Toaster richColors position="top-right" />

      {/* KYC banner */}
      {profile.kyc_prompt_required && (
        <div className="mb-6 border border-rose-200 bg-rose-50 rounded-md p-4 flex items-start gap-3" data-testid="kyc-banner">
          <ShieldAlert size={22} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-rose-900 font-medium text-sm">Complete your VenturaZ KYC</div>
            <div className="text-rose-800/80 text-xs mt-1">
              As an internal team member, you need to verify your identity via DigiLocker before you can act on approvals, deal rooms and sensitive workflows.
              Ask a super_admin to grant you an exemption if this is temporary.
            </div>
          </div>
          <Link
            to="/settings/kyc"
            className="text-xs font-semibold px-3 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-500"
            data-testid="kyc-verify-btn"
          >
            Verify via DigiLocker →
          </Link>
        </div>
      )}

      {/* Status header */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="glass rounded-lg px-4 py-3 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-navy text-gold flex items-center justify-center font-heading text-lg">
            {(profile.name || profile.email || "?")[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-heading text-ink" data-testid="profile-name">{profile.name || "—"}</div>
            <div className="text-xs text-ink-muted font-mono">{profile.email}</div>
          </div>
        </div>
        <Pill tone={kycState === "Verified" ? "success" : kycState === "Exempt" ? "gold" : "danger"} testId="profile-kyc-pill">
          {kycState === "Verified" ? <><ShieldCheck size={11} className="inline mr-1" /> Verified</>
            : kycState === "Exempt" ? "KYC Exempt"
            : "Unverified"}
        </Pill>
        <Pill tone="muted">{profile.role}</Pill>
        {isSuperAdmin && <Pill tone="gold">Super Admin</Pill>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — editable form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="glass rounded-lg p-6">
            <div className="overline mb-4">Personal Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LockableInput
                label="Full name"
                value={profile.name || ""}
                onChange={(v) => setField("name", v)}
                locked={isLocked("name")}
                lockReason={isLocked("name") ? "KYC-verified" : null}
                onRequestChange={() => setChangeReqOpen("name")}
                testId="profile-name-input"
              />
              <LockableInput
                label="Email"
                value={profile.email || ""}
                onChange={() => {}}
                locked={isLocked("email")}
                lockReason={isLocked("email") ? "Admin approval required" : null}
                onRequestChange={() => setChangeReqOpen("email")}
                testId="profile-email-input"
              />
              <Input
                label="Mobile"
                value={profile.mobile || ""}
                onChange={(e) => setField("mobile", e.target.value)}
                testId="profile-mobile-input"
              />
              <LockableInput
                label="Date of birth (DD/MM/YYYY)"
                value={profile.dob || ""}
                onChange={(v) => setField("dob", v)}
                locked={isLocked("dob")}
                lockReason={isLocked("dob") ? "KYC-verified" : null}
                onRequestChange={() => setChangeReqOpen("dob")}
                testId="profile-dob-input"
              />
              <LockableSelect
                label="Gender"
                value={profile.gender || ""}
                onChange={(v) => setField("gender", v)}
                locked={isLocked("gender")}
                lockReason={isLocked("gender") ? "KYC-verified" : null}
                onRequestChange={() => setChangeReqOpen("gender")}
                testId="profile-gender-select"
                options={[
                  { value: "", label: "—" },
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "other", label: "Other" },
                ]}
              />
              <LockableInput
                label="PAN"
                value={profile.pan || ""}
                onChange={(v) => setField("pan", v.toUpperCase())}
                locked={isLocked("pan")}
                lockReason={isLocked("pan") ? "KYC-verified" : null}
                onRequestChange={() => setChangeReqOpen("pan")}
                testId="profile-pan-input"
              />
            </div>
          </div>

          <div className="glass rounded-lg p-6">
            <div className="overline mb-4">Contact & Public Profile</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="City"
                value={profile.city || ""}
                onChange={(e) => setField("city", e.target.value)}
                testId="profile-city-input"
              />
              <Input
                label="LinkedIn URL"
                value={profile.linkedin_url || ""}
                onChange={(e) => setField("linkedin_url", e.target.value)}
                placeholder="https://linkedin.com/in/…"
                testId="profile-linkedin-input"
              />
              <Input
                label="Avatar URL"
                value={profile.avatar_url || ""}
                onChange={(e) => setField("avatar_url", e.target.value)}
                placeholder="https://…"
                testId="profile-avatar-input"
              />
              <Input
                label="Timezone"
                value={profile.timezone || ""}
                onChange={(e) => setField("timezone", e.target.value)}
                placeholder="Asia/Kolkata"
                testId="profile-timezone-input"
              />
              <div className="md:col-span-2">
                <TextArea
                  label="Address"
                  value={profile.address || ""}
                  onChange={(e) => setField("address", e.target.value)}
                  testId="profile-address-input"
                />
              </div>
              <div className="md:col-span-2">
                <TextArea
                  label="Bio (optional)"
                  value={profile.bio || ""}
                  onChange={(e) => setField("bio", e.target.value)}
                  testId="profile-bio-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="gold" onClick={save} disabled={saving} data-testid="profile-save-btn">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        {/* Right — change requests + KYC panel */}
        <div className="space-y-6">
          <div className="glass rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Change Requests</div>
              <Pill tone="muted">{requests.filter((r) => r.status === "Pending").length} pending</Pill>
            </div>
            {requests.length === 0 ? (
              <div className="text-xs text-ink-muted">
                Locked fields (marked with <Lock size={11} className="inline" />) can be changed by raising a request here.
                An admin will review it and update your profile.
              </div>
            ) : (
              <div className="space-y-2">
                {requests.map((r) => (
                  <div key={r.request_id} className="border border-navy/10 rounded-md p-3" data-testid={`change-req-${r.request_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-mono font-semibold text-ink">{r.field.toUpperCase()}</div>
                      <Pill tone={
                        r.status === "Approved" ? "success"
                          : r.status === "Rejected" ? "danger"
                          : r.status === "Cancelled" ? "muted"
                          : "warn"
                      }>{r.status}</Pill>
                    </div>
                    <div className="text-xs text-ink-soft">
                      <span className="text-ink-muted">from</span> <span className="font-mono">{r.current_value || "—"}</span>
                      <span className="text-ink-muted"> → </span>
                      <span className="font-mono text-ink">{r.new_value}</span>
                    </div>
                    {r.reason && <div className="text-[11px] text-ink-muted mt-1 italic">{r.reason}</div>}
                    {r.review_note && <div className="text-[11px] text-emerald-700 mt-1">Reviewer note: {r.review_note}</div>}
                    {r.status === "Pending" && (
                      <button
                        onClick={async () => {
                          try {
                            await api.delete(`/profile/me/change-request/${r.request_id}`);
                            toast.success("Request cancelled");
                            load();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="mt-1 text-[11px] text-rose-600 hover:underline"
                        data-testid={`change-req-cancel-${r.request_id}`}
                      >Cancel request</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isInternal && (
            <div className="glass rounded-lg p-5">
              <div className="overline mb-2">KYC Verification</div>
              <div className="text-xs text-ink-muted mb-3">
                Internal VenturaZ users are required to complete DigiLocker OKYC.
                Once verified, your identity fields become locked — any future changes need admin approval.
              </div>
              {kycState === "Verified" ? (
                <div className="flex items-center gap-2 text-emerald-700 text-sm">
                  <ShieldCheck size={16} /> Verified via {profile.kyc_verified_via || "DigiLocker"}
                </div>
              ) : kycState === "Exempt" ? (
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <ShieldCheck size={16} /> Exempt by super_admin
                </div>
              ) : (
                <Link
                  to="/settings/kyc"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-gold text-navy rounded-md text-sm font-semibold hover:bg-gold-hover"
                  data-testid="kyc-start-link"
                >
                  Start DigiLocker OKYC →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Change-request modal */}
      {changeReqOpen && (
        <ChangeRequestModal
          field={changeReqOpen}
          currentValue={
            changeReqOpen === "email" ? profile.email
              : profile[`kyc_verified_${changeReqOpen}`] || profile[changeReqOpen] || ""
          }
          onClose={() => setChangeReqOpen(null)}
          onSubmitted={() => { setChangeReqOpen(null); load(); }}
        />
      )}
    </DashboardShell>
  );
}

// -----------------------------------------------------------------------
// Lockable input — shows a lock icon + "Request change" when locked
// -----------------------------------------------------------------------
function LockableInput({ label, value, onChange, locked, lockReason, onRequestChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="overline">{label}</div>
        {locked && (
          <button
            onClick={onRequestChange}
            className="text-[10px] font-mono text-gold hover:underline flex items-center gap-1"
            data-testid={`${testId}-request-change`}
          >
            <Lock size={10} /> Request change
          </button>
        )}
      </div>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={locked}
        className={`w-full border px-3 py-2.5 text-sm font-body outline-none transition-all rounded-md ${
          locked
            ? "bg-navy/[0.03] border-navy/10 text-ink-muted cursor-not-allowed"
            : "bg-white border-navy/15 text-ink focus:border-gold focus:ring-2 focus:ring-gold/30"
        }`}
      />
      {locked && lockReason && (
        <div className="text-[10px] text-ink-muted mt-1 font-mono">🔒 {lockReason}</div>
      )}
    </div>
  );
}

function LockableSelect({ label, value, onChange, locked, lockReason, onRequestChange, testId, options }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="overline">{label}</div>
        {locked && (
          <button
            onClick={onRequestChange}
            className="text-[10px] font-mono text-gold hover:underline flex items-center gap-1"
            data-testid={`${testId}-request-change`}
          >
            <Lock size={10} /> Request change
          </button>
        )}
      </div>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={locked}
        className={`w-full border px-3 py-2.5 text-sm font-body outline-none transition-all rounded-md ${
          locked
            ? "bg-navy/[0.03] border-navy/10 text-ink-muted cursor-not-allowed"
            : "bg-white border-navy/15 text-ink focus:border-gold"
        }`}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {locked && lockReason && (
        <div className="text-[10px] text-ink-muted mt-1 font-mono">🔒 {lockReason}</div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Change-request modal
// -----------------------------------------------------------------------
function ChangeRequestModal({ field, currentValue, onClose, onSubmitted }) {
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/profile/me/change-request", { field, new_value: newValue, reason });
      toast.success("Request submitted to admins");
      onSubmitted?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Request change: ${field}`} subtitle="Admin approval required" size="sm" testId="change-req-modal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <div className="overline mb-1">Current value</div>
          <div className="text-sm text-ink font-mono border border-navy/10 rounded-md px-3 py-2 bg-navy/[0.03]">
            {currentValue || "—"}
          </div>
        </div>
        <Input
          label="New value"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          required
          testId="change-req-new-value"
        />
        <TextArea
          label="Reason (optional but helpful)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          testId="change-req-reason"
        />
        {field === "email" && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            Email changes require admin approval regardless of KYC status.
          </div>
        )}
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="gold" disabled={submitting} data-testid="change-req-submit">
            <span className="inline-flex items-center gap-1.5">
              <Send size={13} /> {submitting ? "Submitting…" : "Submit request"}
            </span>
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
